/** Home: start a game, or join one. Big targets, no keyboard hunting. scope.md §8.1. */

import { useState } from 'react';
import { plural } from '@garbage/shared';
import { CODE_ALPHABET, normaliseCode } from '../codes.js';
import { useT } from '../i18n.js';
import { SettingsButton } from './SettingsSheet.js';
import { loadWins } from '../wins.js';
import { loadName, saveName } from '../settings.js';

export function Home({
  onCreate, onJoin, initialCode, onSettings,
}: {
  onCreate: (name: string) => void;
  onJoin: (code: string, name: string) => void;
  initialCode: string | null;
  onSettings?: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(loadName);
  const [code, setCode] = useState(initialCode ?? '');
  const [mode, setMode] = useState<'menu' | 'join'>(initialCode ? 'join' : 'menu');

  /*
   * A name is required — an initial will do — so the table can tell people apart. This
   * device remembers it for next time, and it can be changed right here; it is never
   * kept anywhere else. Ben's call, 1 October 2026.
   */
  const cleanName = name.trim();
  const hasName = cleanName.length > 0;
  const validCode = normaliseCode(code);
  const wins = loadWins();

  return (
    <main className="relative flex h-full flex-col items-center justify-center gap-6 px-6">
      {onSettings && (
        <div className="absolute right-4 top-[calc(1rem+var(--pad-top))]">
          <SettingsButton onClick={onSettings} />
        </div>
      )}
      <h1 className="text-5xl font-black tracking-tight text-sun">Garbage</h1>
      <p className="-mt-4 text-sm text-bone/60">{t('home.tagline')}</p>

      <label className="w-full max-w-xs">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-bone/50">
          {t('home.yourName')}
        </span>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={16}
          placeholder={t('home.namePlaceholder')}
          required
          aria-invalid={!hasName}
          autoComplete="off"
          data-testid="name-input"
          className="w-full rounded-xl bg-white/10 px-4 py-3 text-lg text-bone placeholder:text-bone/30"
        />
        {!hasName && (
          <span className="mt-1 block text-xs text-bone/60" data-testid="name-needed">{t('home.nameNeeded')}</span>
        )}
      </label>

      {mode === 'menu' && (
        <div className="flex w-full max-w-xs flex-col gap-3">
          <button
            type="button"
            data-testid="start-game"
            disabled={!hasName}
            onClick={() => {
              if (!hasName) return;
              saveName(cleanName);
              onCreate(cleanName);
            }}
            className="rounded-2xl bg-sun py-4 text-xl font-black text-ink shadow-lg active:scale-95 disabled:opacity-30"
          >
            {t('home.start')}
          </button>
          <button
            type="button"
            data-testid="join-game"
            onClick={() => setMode('join')}
            className="rounded-2xl bg-white/10 py-4 text-xl font-black active:scale-95"
          >
            {t('home.join')}
          </button>
        </div>
      )}

      {mode === 'join' && (
        <div className="w-full max-w-xs">
          <p className="mb-2 text-center text-sm text-bone/60">{t('home.enterCode')}</p>
          <p
            data-testid="code-display"
            className="mb-3 text-center font-mono text-4xl font-black tracking-[0.3em] text-sun"
          >
            {code.padEnd(4, '·')}
          </p>

          {/* A big pad, because typing on a phone keyboard is miserable for a child. */}
          <div className="grid grid-cols-6 gap-1.5">
            {CODE_ALPHABET.split('').map((letter) => (
              <button
                key={letter}
                type="button"
                data-testid={`key-${letter}`}
                onClick={() => setCode((current) => (current + letter).slice(0, 4))}
                className="rounded-lg bg-white/10 py-2 text-lg font-bold active:bg-sun active:text-ink"
              >
                {letter}
              </button>
            ))}
          </div>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              data-testid="code-back"
              onClick={() => setCode((current) => current.slice(0, -1))}
              className="flex-1 rounded-xl bg-white/10 py-3 font-bold"
            >
              {t('home.delete')}
            </button>
            <button
              type="button"
              data-testid="code-go"
              disabled={validCode === null || !hasName}
              onClick={() => {
                if (!validCode || !hasName) return;
                saveName(cleanName);
                onJoin(validCode, cleanName);
              }}
              className="flex-[2] rounded-xl bg-sun py-3 text-lg font-black text-ink disabled:opacity-30"
            >
              {t('home.joinButton')}
            </button>
          </div>
        </div>
      )}
      {wins > 0 && (
        <p className="text-sm font-bold text-bone/70" data-testid="wins-total">
          🏆 {plural(t, 'home.wins', wins, { count: wins })}
        </p>
      )}
    </main>
  );
}
